// Dashboard "Settings" load + save orchestration. This lets a business owner
// amend the branding/profile they set during sign-up.
//
// The write shapes here MIRROR onboardingHandler.js exactly so an amend
// round-trips cleanly against the same RTDB paths the sign-up flow wrote:
//   - top-level `displayName` + `thresholdForRedeeming`
//   - `rewardsCardData` (rgb colours, labelColor, aux fields - blank values
//     are written back as the " " sentinel onboarding uses)
//   - `webDisplayData` (title/subtitle + the same colours, so the sign-up
//     page stays colour-synced with the loyalty card - exactly what the
//     web-page editor does on save).
//
// Every read/write is keyed by the passed `businessId`; we never touch any
// other business's node.

import {
  getBusinessSnapshot,
  getCurrentUserRole,
  updateRewardsCardBranding,
  updateWebDisplayData,
  updateBusinessProfile,
} from "./firebase-business.js";
import { getLogoImageUrl, uploadLogoImage } from "./firebase-storage.js";
import { updateSettingsBranding } from "./firebase-functions.js";
import { authStore } from "./stores/authStore.js";
import { hexToRgb, rgbToHex } from "./utils/colorUtils.js";
import { track } from "./analytics.js";

// Trims the " " sentinel that the sign-up flow writes for empty aux fields
// back into a real empty string for the editor model.
const unsentinel = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : "";

// Loads the canonical editor model for the Settings page. Returns
// { form, role, logoUrl }. `form` holds HEX colours so it can bind straight
// to <input type="color">; colours are converted back to rgb on save.
//
// Throws if the business node is missing so the page can show its
// load-error state. The logo read is tolerant - a business may not have a
// logo, so a miss resolves to "".
export const loadBusinessSettings = async (businessId) => {
  if (!businessId) throw new Error("businessId is required");

  const [snapshot, role, logoUrl] = await Promise.all([
    getBusinessSnapshot(businessId),
    getCurrentUserRole(businessId, authStore.user?.email || ""),
    getLogoImageUrl(businessId).catch(() => ""),
  ]);

  if (snapshot == null) {
    throw new Error("Business not found");
  }

  const card = snapshot.rewardsCardData || {};
  const web = snapshot.webDisplayData || {};

  const thresholdNumeric = Number(snapshot.thresholdForRedeeming);
  // Absent until the business first changes it, so a miss means 0.
  const startStampNumeric = Number(snapshot.startStampCount);

  const form = {
    displayName: card.displayName || snapshot.displayName || "",
    backgroundColor: rgbToHex(card.backgroundColor) || "#ffffff",
    foregroundColor: rgbToHex(card.foregroundColor) || "#000000",
    thresholdForRedeeming: Number.isFinite(thresholdNumeric)
      ? thresholdNumeric
      : 8,
    startStampCount:
      Number.isFinite(startStampNumeric) && startStampNumeric > 0
        ? startStampNumeric
        : 0,
    auxiliaryFieldLabel: unsentinel(card.auxiliaryFieldLabel),
    auxiliaryFieldValue: unsentinel(card.auxiliaryFieldValue),
    title: web.title || "",
    subtitle: web.subtitle || "",
  };

  return { form, role: role || "Employee", logoUrl: logoUrl || "" };
};

// Persists the Settings editor model. Treat this as one logical save - we
// let any error propagate so the page can surface it. The order matters:
// the logo upload happens first (so `logoImageName` only gets written once
// the bytes are stored), then the three RTDB slices are merged.
export const saveBusinessSettings = async (
  businessId,
  form,
  logoFile,
  previousForm = null
) => {
  if (!businessId) throw new Error("businessId is required");

  // a. Upload the new logo first, if one was supplied.
  if (logoFile) {
    await uploadLogoImage(businessId, logoFile);
  }

  // b. Convert the editor HEX colours back to the rgb strings RTDB stores.
  const bgRgb = hexToRgb(form.backgroundColor);
  const fgRgb = hexToRgb(form.foregroundColor);

  // b2. Decide whether this save touches the "Brand & rewards card" tab. Only
  //    those edits regenerate stamp images / update the Wallet class AND bump
  //    the cache-bust version - sign-up-page-only edits (title/subtitle) must
  //    not. Without a baseline we can't diff, so we fall back to "logo only".
  //    NOTE: brandingVersion is deliberately NOT in this key set - it must not
  //    affect dirty-detection or this change gate. Neither is startStampCount:
  //    it changes nothing about how the card LOOKS, so it must not trigger a
  //    stamp-image regeneration / Wallet class push.
  const REWARDS_TAB_KEYS = [
    "displayName",
    "backgroundColor",
    "foregroundColor",
    "thresholdForRedeeming",
    "auxiliaryFieldLabel",
    "auxiliaryFieldValue",
  ];
  const brandingOrRewardsChanged =
    !!logoFile ||
    (!!previousForm &&
      REWARDS_TAB_KEYS.some((key) => previousForm[key] !== form[key]));

  // Coerce the threshold once here so the backend payload matches the
  // Number(...) coercion used for the RTDB write below. The field can arrive
  // as "" (BrandingEditor emits an empty string when cleared), which yields NaN.
  const redeemThreshold = Number(form.thresholdForRedeeming);

  // We only hand off to the backend (and bump the cache-bust version) when the
  // tab changed AND the threshold is a usable number. canSave already disables
  // Save for invalid thresholds, but saveBusinessSettings has no internal
  // validation, so guard here so we never persist a bumped version that no
  // freshly-generated images exist for - keep the bump + endpoint together.
  const willUpdateBranding =
    brandingOrRewardsChanged && Number.isFinite(redeemThreshold);

  // Bump the monotonic cache-bust version ONLY when we're going to regenerate
  // images. Read the current value fresh from RTDB at save time (rather than
  // threading it through the form) to avoid a stale-baseline race if two saves
  // overlap - the source of truth is rewardsCardData.brandingVersion, with an
  // absent/invalid value treated as 0. The same newVersion is both persisted to
  // RTDB (below) and sent as `version` to the endpoint, and the RTDB write
  // happens before the endpoint call so prod Android pass-generation (which
  // reads brandingVersion from RTDB) stays consistent with the pushed class.
  let newVersion = null;
  // The live Android pass builds its loyalty classId from the business's
  // `passVersion`, NOT a fixed _v1 (see generatedAndroidPass.js:
  // `client_${businessId}_v${passVersion}`). We must update that SAME class, so
  // read passVersion from RTDB; default to 1 to match a brand-new business's
  // first class. (passVersion is the CLASS id suffix; brandingVersion above is
  // the separate image cache-bust counter - do not conflate them.)
  let passVersion = 1;
  if (willUpdateBranding) {
    const snap = await getBusinessSnapshot(businessId);
    const currentVersion = Number(snap?.rewardsCardData?.brandingVersion) || 0;
    newVersion = currentVersion + 1;
    passVersion = snap?.rewardsCardData?.passVersion ?? 1;
  }

  // c. rewardsCardData (the loyalty card) + top-level thresholdForRedeeming.
  await updateRewardsCardBranding(businessId, {
    displayName: form.displayName,
    backgroundColor: bgRgb,
    foregroundColor: fgRgb,
    labelColor: fgRgb,
    auxiliaryFieldLabel: (form.auxiliaryFieldLabel || "").trim() || " ",
    auxiliaryFieldValue: (form.auxiliaryFieldValue || "").trim() || " ",
    thresholdForRedeeming: Number(form.thresholdForRedeeming),
    ...(logoFile ? { logoImageName: `${businessId}.png` } : {}),
    // Stored as a STRING (RTDB) - backend reads it via Number(...) so the
    // numeric round-trip (read -> +1 -> persist) is unaffected.
    ...(newVersion !== null ? { brandingVersion: String(newVersion) } : {}),
  });

  // d. webDisplayData (the customer sign-up page) - kept colour-synced with
  //    the card, mirroring the web-page editor.
  await updateWebDisplayData(businessId, {
    title: form.title || "",
    subtitle: form.subtitle || "",
    backgroundColor: bgRgb,
    textColor: fgRgb,
  });

  // e. Top-level profile fields outside the two slices (displayName is also
  //    mirrored at the top level, which the card/web editors don't cover).
  //    This is also where the start stamp count lands - it creates
  //    `businesses/{businessId}/startStampCount` the first time a business
  //    changes it. An unusable value is skipped rather than written, so a
  //    stored count is never clobbered with NaN.
  const startStampCount = Number(form.startStampCount);
  await updateBusinessProfile(businessId, {
    displayName: form.displayName,
    ...(Number.isFinite(startStampCount)
      ? { startStampCount: Math.max(0, Math.round(startStampCount)) }
      : {}),
  });

  // f. When the "Brand & rewards card" tab changed (colours, name, threshold,
  //    aux fields, or a new logo) AND the threshold is usable,
  //    hand off to the single backend endpoint that regenerates the stale stamp
  //    ("circle") images AND updates the Google Wallet loyalty class for THIS
  //    business. Best-effort - a failure must not fail the save (the RTDB writes
  //    already succeeded; it can be retried on a later save). The gate
  //    (willUpdateBranding) and the bumped version were computed above so the
  //    persisted brandingVersion and the `version` sent here are the SAME value.
  if (willUpdateBranding) {
    try {
      await updateSettingsBranding({
        businessId,
        // Cache-bust version Android uses to refresh logo/stamp image URLs.
        // Same value just persisted to rewardsCardData.brandingVersion above.
        version: newVersion,
        // The classId the LIVE pass uses: `client_${id}_v${passVersion}` (read
        // from RTDB above). Hardcoding _v1 here updated a stale class that no
        // pass references (e.g. a business on passVersion 2 uses ..._v2).
        classId: `3388000000022951634.client_${businessId}_v${passVersion}`,
        businessName: form.displayName,
        // Assumes the form holds #rrggbb hex colours - <input type="color">
        // guarantees that shape and rgbToHex normalizes them on load. The
        // backend passes hexBackgroundColor straight to Google Wallet, which
        // requires #rrggbb.
        hexBackgroundColor: form.backgroundColor,
        logoImageName: `${businessId}.png`,
        stampData: {
          businessId,
          count: 0,
          redeemThreshold,
          foregroundColor: form.foregroundColor,
          backgroundColor: form.backgroundColor,
        },
      });
    } catch (e) {
      // Swallow - the RTDB writes already succeeded.
      console.error("Settings branding update failed:", e?.message || e);
    }
  }

  track("dashboard_settings_saved", { business_id: businessId });
};
